import { useDark } from '@vueuse/core'
import { defineStore } from 'pinia'

import { Account, OpenAPI } from '@/client'
import { SubscriptionInfo } from '@/types/billing'
import { useSessionStore } from './sessionStore'

export enum Mode {
  Demo = 'demo', // if the user is in demo-data mode
  Trial = 'trial',
  Paid = 'paid',
  Default = 'default',
  Dev = 'dev',
}

type TutorialType = {
  maxStepCount: number
  currentSectionIndex: number
  currentStepIndex: number
}

type GlobalStore = {
  uploadEndpoint: string
  productionApi: string
  productionUsApi: string
  stagingApi: string
  staging2Api: string
  developmentApi: string
  betaApi: string

  mode: Mode[]

  account: Account | null

  // Dialogs
  showOnboardingDialog: boolean
  showAuthFlowDialog: boolean
  showReauthenticateDialog: boolean

  // Drawers
  drawers: string[]
  drawerClosers: Record<string, () => void>

  // Current Popup
  currentPopup: string | null

  // Debugging
  showDebugModal: boolean
  showDebugModalFPS: boolean
  useAccountStreaming: boolean

  // User related
  user: {
    jwt?: string
    user?: Account
    tutorial?: TutorialType
    language: string
    subscription?: SubscriptionInfo
  }

  // Settings related
  settings: {
    isDark: boolean
  }
}

const isDark = useDark()

export const useGlobalStore = defineStore('globalStore', {
  state: (): GlobalStore => ({
    // Connection data
    uploadEndpoint: 'https://vps1.saasmetrix.de:2100/file-upload',
    productionApi: 'https://api.saasmetrix.io',
    productionUsApi: 'https://api.us.saasmetrix.io',
    stagingApi: 'https://api.staging.intern.saasmetrix.io',
    staging2Api: 'https://api.staging2.intern.saasmetrix.io',
    developmentApi: 'https://dev.saasmetrix.io',
    betaApi: 'https://beta-api.saasmetrix.io',

    // Dialogs
    showOnboardingDialog: false,
    showDebugModal: false,
    showDebugModalFPS: false,
    showAuthFlowDialog: false,
    showReauthenticateDialog: false,

    // Drawers
    drawers: [],
    drawerClosers: {},

    useAccountStreaming: false,

    mode: [Mode.Default],

    // Current Account
    account: null,

    // Current Popup
    currentPopup: null,

    // User related
    user: {
      jwt: undefined,
      language: 'de',
    },

    // Settings related
    settings: {
      isDark: isDark.value,
    },
  }),
  getters: {
    state: (state) => state,
    getIp: (state) => {
      const sessionStore = useSessionStore()
      const companyRegion = sessionStore.currentCompany?.region

      switch (import.meta.env.MODE) {
        case 'development':
          return state.developmentApi
        case 'production':
          if (companyRegion === 'US') {
            return state.productionUsApi
          }
          return state.productionApi
        case 'staging':
          return state.stagingApi
        case 'staging2':
          return state.staging2Api
        case 'beta':
          return state.productionApi
        case 'localhost':
          return 'http://127.0.0.1:8000'
        default:
          return 'http://127.0.0.1:8000'
      }
    },
    topDrawerId: (s) => s.drawers.at(-1) ?? null,
  },
  actions: {
    // TODO: Remove this -> https://github.com/saasmetrix/saasmetrix/issues/5861
    getJWT(): string | undefined {
      if (OpenAPI.TOKEN) return OpenAPI.TOKEN as string
      return undefined
    },

    setTutorial(tutorial: TutorialType | undefined): void {
      this.user.tutorial = tutorial
    },
    setIsDark(value: boolean): void {
      this.settings.isDark = value
    },

    // Drawers
    registerDrawer(id: string, closer: () => void) {
      if (!this.drawers.includes(id)) {
        this.drawers.push(id)
        this.drawerClosers[id] = closer
      }
    },

    unregisterDrawer(id: string) {
      this.drawers = this.drawers.filter((d) => d !== id)
      delete this.drawerClosers[id]
    },
    getDrawerLevel(id: string): number {
      const idx = this.drawers.indexOf(id)
      return idx === -1 ? 0 : idx + 1
    },
    closeTopDrawer() {
      const id = this.drawers.at(-1)
      if (id) this.drawerClosers[id]?.() // call the closer function
    },
    closeAllDrawers() {
      this.drawers.forEach((i) => {
        this.drawerClosers[i]?.()
      })
    },
  },
})
